/**
 * Fretboard: classical guitar samples from `public/` (WAVs + `guitar-sounds-manifest.json`).
 * The manifest is loaded with `fetch` at first note so it always matches disk and shows in DevTools Network.
 */

export const NOTE_FREQ_EQ_TEMPERED: Record<string, number> = {
  C: 261.6255653005986,
  'C#': 277.1826309768721,
  D: 293.6647679174076,
  'D#': 311.1269837220809,
  E: 329.6275569128699,
  F: 349.2282314330039,
  'F#': 369.9944227116344,
  G: 391.99543598174927,
  'G#': 415.3046975799451,
  A: 440.0,
  'A#': 466.1637615180899,
  B: 493.8833013781249,
};

export function noteFrequencyAtOctave(note: string, octave: number): number | null {
  const base = NOTE_FREQ_EQ_TEMPERED[note];
  if (base == null) return null;
  return base * Math.pow(2, octave - 4);
}

export type PluckOptions = {
  peakGain?: number;
  durationScale?: number;
  destination?: AudioNode;
};

/** Await after a user gesture so scheduled audio actually runs (Chrome autoplay policy). */
export async function resumeAudioContext(ctx: AudioContext): Promise<void> {
  if (ctx.state === 'suspended') {
    await ctx.resume();
  }
}

/** Standard tuning open-string MIDI (string index 0 = high E … 5 = low E). */
const OPEN_STRING_MIDI: readonly number[] = [64, 59, 55, 50, 45, 40];

const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'] as const;

/**
 * Pick a playable string/fret for a MIDI pitch (same convention as the fretboard).
 * Prefers mid-neck positions where samples are usually most reliable.
 */
export function pickStringFretForMidi(
  targetMidi: number,
  opts?: { maxFret?: number }
): { stringIdx: number; fret: number } | null {
  const midi = Math.round(targetMidi);
  if (!Number.isFinite(midi)) return null;
  const maxFret = opts?.maxFret ?? 22;
  const preferOrder = [3, 4, 2, 5, 1, 0] as const;
  for (const stringIdx of preferOrder) {
    const fret = midi - OPEN_STRING_MIDI[stringIdx];
    if (fret >= 0 && fret <= maxFret) return { stringIdx, fret };
  }
  for (let stringIdx = 0; stringIdx < 6; stringIdx++) {
    const fret = midi - OPEN_STRING_MIDI[stringIdx];
    if (fret >= 0 && fret <= maxFret) return { stringIdx, fret };
  }
  return null;
}

type ManifestMeta = { publicSubdir?: string };

type GuitarManifestState = {
  publicSubdir: string;
  samples: Record<string, string[]>;
  byMidi: Map<number, string[]>;
};

function normalizeFetchedJsonRoot(raw: unknown): Record<string, unknown> {
  if (
    raw &&
    typeof raw === 'object' &&
    'default' in (raw as object) &&
    typeof (raw as { default: unknown }).default === 'object' &&
    (raw as { default: unknown }).default !== null
  ) {
    return (raw as { default: Record<string, unknown> }).default;
  }
  return raw as Record<string, unknown>;
}

function parseManifestRaw(raw: Record<string, unknown>): GuitarManifestState {
  const meta = (raw._meta as ManifestMeta | undefined) ?? {};
  const publicSubdir = meta.publicSubdir?.trim() || 'Guitar Sounds';
  const samples: Record<string, string[]> = {};
  for (const [k, v] of Object.entries(raw)) {
    if (k === '_meta' || !Array.isArray(v)) continue;
    samples[k] = v as string[];
  }
  const byMidi = new Map<number, string[]>();
  for (const [key, files] of Object.entries(samples)) {
    const parts = key.split('_');
    if (parts.length !== 2) continue;
    const gStr = parseInt(parts[0], 10);
    const fret = parseInt(parts[1], 10);
    if (gStr < 1 || gStr > 6 || fret < 0 || !Number.isFinite(fret)) continue;
    const midi = OPEN_STRING_MIDI[gStr - 1] + fret;
    const cur = byMidi.get(midi) ?? [];
    const set = new Set(cur);
    for (const f of files) set.add(f);
    byMidi.set(midi, Array.from(set));
  }
  return { publicSubdir, samples, byMidi };
}

function manifestUrl(): string {
  const pub = String(process.env.PUBLIC_URL ?? '');
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const base = `${origin}${pub.endsWith('/') ? pub : `${pub}/`}`;
  return new URL('guitar-sounds-manifest.json', base).href;
}

let guitarManifest: GuitarManifestState | null = null;
let manifestInflight: Promise<GuitarManifestState | null> | null = null;

async function ensureGuitarManifestLoaded(): Promise<GuitarManifestState | null> {
  if (guitarManifest) return guitarManifest;
  if (typeof window === 'undefined') return null;
  if (!manifestInflight) {
    manifestInflight = (async () => {
      try {
        const res = await fetch(manifestUrl(), { cache: 'no-store' });
        if (!res.ok) throw new Error(`manifest ${res.status}`);
        const raw = normalizeFetchedJsonRoot(await res.json());
        guitarManifest = parseManifestRaw(raw);
        return guitarManifest;
      } catch {
        return null;
      } finally {
        manifestInflight = null;
      }
    })();
  }
  return manifestInflight;
}

function guitarSampleAbsoluteUrl(publicSubdir: string, filename: string): string {
  const pub = String(process.env.PUBLIC_URL ?? '');
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const segment = `${publicSubdir}/${filename}`;
  if (!origin) {
    const p = pub.replace(/\/$/, '');
    const path = `${p}/${segment}`.replace(/\/{2,}/g, '/');
    return path.startsWith('/') ? path : `/${path}`;
  }
  const base = `${origin}${pub.endsWith('/') ? pub : `${pub}/`}`;
  return new URL(segment, base).href;
}

const sampleBufferByUrl = new Map<string, Promise<AudioBuffer>>();

function loadGuitarSampleBuffer(ctx: AudioContext, absoluteUrl: string): Promise<AudioBuffer> {
  let pending = sampleBufferByUrl.get(absoluteUrl);
  if (!pending) {
    pending = fetch(absoluteUrl)
      .then((res) => {
        if (!res.ok) throw new Error(`sample fetch ${res.status}`);
        return res.arrayBuffer();
      })
      .then((ab) => ctx.decodeAudioData(ab.slice(0)));
    pending.catch(() => {
      sampleBufferByUrl.delete(absoluteUrl);
    });
    sampleBufferByUrl.set(absoluteUrl, pending);
  }
  return pending;
}

function resolveSampleFiles(m: GuitarManifestState, stringIdx: number, guitarFret: number): string[] | undefined {
  const key = `${stringIdx + 1}_${guitarFret}`;
  const exact = m.samples[key];
  if (exact?.length) return exact;
  const midi = OPEN_STRING_MIDI[stringIdx] + guitarFret;
  return m.byMidi.get(midi);
}

/** When no exact sample: pick closest recorded pitch (≤12 semitones) and rate-shift; prefer same string. */
const MAX_STRETCH_SEMITONES = 12;

function findStretchSample(
  m: GuitarManifestState,
  stringIdx: number,
  guitarFret: number
): { filename: string; playbackRate: number } | null {
  const targetMidi = OPEN_STRING_MIDI[stringIdx] + guitarFret;
  const wantString = stringIdx + 1;

  type Cand = {
    diff: number;
    sameString: boolean;
    fretDist: number;
    filename: string;
    sourceMidi: number;
  };
  let best: Cand | null = null;

  for (const [key, fileList] of Object.entries(m.samples)) {
    if (!fileList.length) continue;
    const parts = key.split('_');
    if (parts.length !== 2) continue;
    const gs = parseInt(parts[0], 10);
    const fr = parseInt(parts[1], 10);
    if (gs < 1 || gs > 6 || fr < 0) continue;
    const sourceMidi = OPEN_STRING_MIDI[gs - 1] + fr;
    const diff = Math.abs(targetMidi - sourceMidi);
    if (diff > MAX_STRETCH_SEMITONES) continue;
    const sameString = gs === wantString;
    const fretDist = Math.abs(fr - guitarFret);
    const filename = fileList[Math.floor(Math.random() * fileList.length)];
    const cand: Cand = { diff, sameString, fretDist, filename, sourceMidi };

    if (!best) {
      best = cand;
      continue;
    }
    if (diff < best.diff) {
      best = cand;
      continue;
    }
    if (diff > best.diff) continue;
    if (sameString && !best.sameString) {
      best = cand;
      continue;
    }
    if (sameString === best.sameString && fretDist < best.fretDist) {
      best = cand;
    }
  }

  if (!best) return null;
  const playbackRate = Math.pow(2, (targetMidi - best.sourceMidi) / 12);
  if (playbackRate < 0.5 || playbackRate > 2) return null;
  return { filename: best.filename, playbackRate };
}

export type StringFretPluckOptions = {
  peakGain?: number;
  destination?: AudioNode;
};

function playSynthForStringFret(
  ctx: AudioContext,
  stringIdx: number,
  guitarFret: number,
  when: number,
  opts?: StringFretPluckOptions & Pick<PluckOptions, 'durationScale'>
): void {
  const midi = OPEN_STRING_MIDI[stringIdx] + guitarFret;
  const note = NOTE_NAMES[midi % 12];
  const octave = Math.floor(midi / 12) - 1;
  const frequency = noteFrequencyAtOctave(note, octave);
  if (frequency == null) return;
  playAcousticGuitarPluck(ctx, frequency, when, {
    peakGain: opts?.peakGain ?? 0.29,
    durationScale: opts?.durationScale,
    destination: opts?.destination,
  });
}

/**
 * Play a sampled note (exact string/fret, same MIDI elsewhere, or nearest sample within ±12 semitones via playbackRate).
 * @param stringIdx 0 = high E, 5 = low E (matches app `STRING_TUNINGS` order)
 * @param guitarFret 0 = nut (open), 1 = first fret, …
 */
export function playGuitarStringFret(
  ctx: AudioContext,
  stringIdx: number,
  guitarFret: number,
  when: number,
  opts?: StringFretPluckOptions & Pick<PluckOptions, 'durationScale'>
): void {
  if (stringIdx < 0 || stringIdx > 5 || guitarFret < 0 || !Number.isFinite(guitarFret)) return;

  if (ctx.state === 'suspended') void ctx.resume();

  const dest = opts?.destination ?? ctx.destination;
  const peak = opts?.peakGain ?? 0.32;

  void ensureGuitarManifestLoaded().then((m) => {
    if (!m) {
      playSynthForStringFret(ctx, stringIdx, guitarFret, when, opts);
      return;
    }
    const files = resolveSampleFiles(m, stringIdx, guitarFret);
    let filename: string | null = null;
    let playbackRate = 1;

    if (files?.length) {
      filename = files[Math.floor(Math.random() * files.length)];
    } else {
      const stretched = findStretchSample(m, stringIdx, guitarFret);
      if (stretched) {
        filename = stretched.filename;
        playbackRate = stretched.playbackRate;
      }
    }

    if (!filename) {
      playSynthForStringFret(ctx, stringIdx, guitarFret, when, opts);
      return;
    }

    const url = guitarSampleAbsoluteUrl(m.publicSubdir, filename);
    loadGuitarSampleBuffer(ctx, url)
      .then((buf) => {
        const start = Math.max(when, ctx.currentTime);
        const src = ctx.createBufferSource();
        src.buffer = buf;
        src.playbackRate.setValueAtTime(playbackRate, start);
        const g = ctx.createGain();
        g.gain.setValueAtTime(Math.max(0.0001, peak), start);
        src.connect(g);
        g.connect(dest);
        const playSec = Math.min(buf.duration / playbackRate + 0.04, 4);
        src.start(start);
        src.stop(start + playSec);
      })
      .catch(() => {
        playSynthForStringFret(ctx, stringIdx, guitarFret, when, opts);
      });
  });
}

/**
 * Bright, synth “electric guitar” pluck: detuned saws/triangles + harmonics + click noise.
 */
export function playAcousticGuitarPluck(
  ctx: AudioContext,
  frequencyHz: number,
  when: number,
  opts?: PluckOptions
): void {
  if (ctx.state === 'suspended') void ctx.resume();

  const dest = opts?.destination ?? ctx.destination;
  const peak = opts?.peakGain ?? 0.27;
  const scale = opts?.durationScale ?? 1;
  if (!Number.isFinite(frequencyHz) || frequencyHz <= 0) return;

  const f0 = frequencyHz;
  // Shorter than acoustic KS — electric-style sustain/decay
  const bodySec = Math.min(0.55, Math.max(0.12, 0.08 + 380 / f0) * scale);
  const tailEnd = when + 0.004 + bodySec;

  const master = ctx.createGain();
  master.gain.setValueAtTime(0.0001, when);
  master.gain.linearRampToValueAtTime(peak, when + 0.0025);
  master.gain.setValueAtTime(peak * 0.72, when + 0.004 + bodySec * 0.12);
  master.gain.exponentialRampToValueAtTime(0.0001, tailEnd);

  const comp = ctx.createDynamicsCompressor();
  comp.threshold.value = -22;
  comp.knee.value = 18;
  comp.ratio.value = 3.2;
  comp.attack.value = 0.001;
  comp.release.value = 0.06;

  const toneLP = ctx.createBiquadFilter();
  toneLP.type = 'lowpass';
  toneLP.frequency.setValueAtTime(Math.min(12000, 5200 + f0 * 3.2), when);
  toneLP.frequency.exponentialRampToValueAtTime(Math.max(650, 380 + f0 * 0.55), when + bodySec * 0.92);
  toneLP.Q.value = 0.65;

  const toneHPF = ctx.createBiquadFilter();
  toneHPF.type = 'highpass';
  toneHPF.frequency.value = Math.min(280, 90 + f0 * 0.08);
  toneHPF.Q.value = 0.55;

  comp.connect(toneLP);
  toneLP.connect(toneHPF);
  toneHPF.connect(master);
  master.connect(dest);

  const stopAt = tailEnd + 0.03;

  const addOsc = (
    type: OscillatorType,
    freq: number,
    detuneCents: number,
    partialGain: number
  ): void => {
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, when);
    osc.detune.setValueAtTime(detuneCents, when);
    const g = ctx.createGain();
    g.gain.value = partialGain;
    osc.connect(g);
    g.connect(comp);
    osc.start(when);
    osc.stop(stopAt);
  };

  // Main body — slightly detuned saws for that electric width
  addOsc('sawtooth', f0, -6, 0.14);
  addOsc('sawtooth', f0, 7, 0.12);
  addOsc('triangle', f0 * 2, -3, 0.07);
  addOsc('triangle', f0 * 3, 4, 0.045);
  addOsc('sine', f0 * 4, 0, 0.028);

  // Short pick noise (bright)
  const nLen = Math.floor(ctx.sampleRate * 0.012);
  const nBuf = ctx.createBuffer(1, nLen, ctx.sampleRate);
  const nd = nBuf.getChannelData(0);
  for (let i = 0; i < nLen; i++) {
    const t = i / nLen;
    nd[i] = (Math.random() * 2 - 1) * Math.exp(-t * 22) * (1 - t);
  }
  const nSrc = ctx.createBufferSource();
  nSrc.buffer = nBuf;
  const nBP = ctx.createBiquadFilter();
  nBP.type = 'bandpass';
  nBP.frequency.value = Math.min(6500, 1800 + f0 * 5);
  nBP.Q.value = 0.7;
  const nG = ctx.createGain();
  nG.gain.setValueAtTime(0.0001, when);
  nG.gain.linearRampToValueAtTime(0.11, when + 0.0008);
  nG.gain.exponentialRampToValueAtTime(0.0001, when + 0.018);
  nSrc.connect(nBP);
  nBP.connect(nG);
  nG.connect(comp);
  nSrc.start(when);
  nSrc.stop(when + 0.022);
}

/** Soft strum-position tick */
export function playNylonStrumClick(ctx: AudioContext, when: number, velocity = 1): void {
  const peak = 0.11 * velocity;
  const nLen = Math.floor(ctx.sampleRate * 0.018);
  const nBuf = ctx.createBuffer(1, nLen, ctx.sampleRate);
  const nd = nBuf.getChannelData(0);
  for (let i = 0; i < nLen; i++) {
    const t = i / nLen;
    nd[i] = (Math.random() * 2 - 1) * Math.exp(-t * 18) * (1 - t * 0.5);
  }
  const nSrc = ctx.createBufferSource();
  nSrc.buffer = nBuf;
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = 1900;
  bp.Q.value = 0.65;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, when);
  g.gain.linearRampToValueAtTime(peak, when + 0.0016);
  g.gain.exponentialRampToValueAtTime(0.0001, when + 0.035);
  nSrc.connect(bp);
  bp.connect(g);
  g.connect(ctx.destination);
  nSrc.start(when);
  nSrc.stop(when + 0.04);

  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.value = 155;
  const og = ctx.createGain();
  og.gain.setValueAtTime(0.0001, when);
  og.gain.linearRampToValueAtTime(peak * 0.35, when + 0.002);
  og.gain.exponentialRampToValueAtTime(0.0001, when + 0.048);
  osc.connect(og);
  og.connect(ctx.destination);
  osc.start(when);
  osc.stop(when + 0.055);
}
